import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import {
  ManageContactsEditRequest,
  ManageContactsEditResponse,
  ManageContactsEditWireRequest,
  SavedPermissionEntry,
} from "@manageContacts/store/models";
import { buildPermissionIdLookups, toEditWireRequest } from "@manageContacts/utils/editPermissions";

export interface SaveContactPermissionsArg {
  request: ManageContactsEditRequest;
  /**
   * Post-save permission snapshot per dirty row. Used by `mergeContactPermissions`
   * to reconcile the contacts slice locally so the table re-renders with saved
   * values once the thunk fulfils.
   */
  savedRows: SavedPermissionEntry[];
}

/**
 * Contact-service-api endpoint that persists Manage Contacts permission edits.
 * Mirrors the My Profile save pattern (`contacts/v1/user/Profile`): a relative
 * path resolved against the global `REACT_APP_API_BASE_URI` axios instance.
 * The `entitledUserId` query parameter is appended automatically by
 * `RequestService.configureGlobalQueryParams` from the current (or impersonated)
 * user, so callers do not pass it explicitly.
 */
const SAVE_PERMISSIONS_URL = "contacts/v1/user/ManageContacts/Permissions";

/**
 * Persist Manage Contacts permission edits via the contact-service-api.
 *
 * POSTs the dirty rows (translated from display strings to numeric
 * `serviceId` / `notificationId` values via `toEditWireRequest`) to
 * `${apiBase}/contacts/v1/user/ManageContacts/Permissions?entitledUserId=<id>`
 * using the JSON-Patch content type the audit endpoint expects.
 *
 * The page's edit-mode state machine carries permissions as display strings
 * (e.g. "Capital Call"); the endpoint expects numeric ids. We derive the
 * display → id mapping from the contacts GET response already in the slice
 * (`buildPermissionIdLookups`) so callers never have to know about ids.
 */
export const saveContactPermissionsThunk = createAsyncThunk<
  ManageContactsEditResponse,
  SaveContactPermissionsArg,
  ThunkApiConfig
>("manageContacts/saveContactPermissions", async ({ request }, { extra, getState, rejectWithValue }) => {
  try {
    const contacts = getState().manageContacts.contacts.data?.contacts ?? [];
    const lookups = buildPermissionIdLookups(contacts);
    const wireRequest = toEditWireRequest(request, lookups);

    const response = await extra<ManageContactsEditResponse, ManageContactsEditWireRequest>(SAVE_PERMISSIONS_URL, {
      method: "POST",
      body: wireRequest,
      headers: {
        "Content-Type": "application/json-patch+json",
      },
    });

    if (!response || !response.data) {
      return rejectWithValue("Failed to save contact permissions");
    }

    return response.data;
  } catch (error) {
    console.error("[Manage Contacts API] Error saving permissions:", error);

    return rejectWithValue("Failed to save contact permissions");
  }
});
